export type PlanId = 'starter' | 'growth' | 'pro';

export type PlanFeatureFlag =
  | 'cardPayments'
  | 'analytics'
  | 'promotions'
  | 'flashSales'
  | 'bundles'
  | 'classifieds'
  | 'storeBanner'
  | 'featuredSlots';

export interface PlanDefinition {
  id: PlanId;
  name: string;
  monthlyPriceCents: number;
  commissionBps: number;
  maxProductListings: number | null;
  maxClassifiedListings: number | null;
  maxPropertyListings: number | null;
  features: PlanFeatureFlag[];
  marketingFeatures: string[];
  notIncluded?: string[];
  badge?: string;
  highlight?: boolean;
  active: boolean;
}

export const DEFAULT_PLAN_DEFINITIONS: PlanDefinition[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPriceCents: 0,
    commissionBps: 1000,
    maxProductListings: 10,
    maxClassifiedListings: 10,
    maxPropertyListings: 0,
    features: ['classifieds'],
    marketingFeatures: [
      'Up to 10 product listings',
      'Up to 10 classified ads',
      '10% platform commission',
      'Basic store page',
      'Cash on delivery payments',
      'Order management dashboard',
      'Customer messaging',
    ],
    notIncluded: ['Card payment processing', 'Featured listing slots', 'Sales analytics'],
    active: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    monthlyPriceCents: 9900,
    commissionBps: 800,
    maxProductListings: 75,
    maxClassifiedListings: 20,
    maxPropertyListings: 25,
    badge: 'Most popular',
    highlight: true,
    features: [
      'cardPayments',
      'analytics',
      'promotions',
      'flashSales',
      'bundles',
      'classifieds',
      'storeBanner',
      'featuredSlots',
    ],
    marketingFeatures: [
      'Up to 75 product listings',
      'Up to 20 classified ads',
      'Up to 25 property listings',
      '8% platform commission',
      'Custom store banner & bio',
      'Card + cash on delivery',
      '1 featured listing slot/month',
      'Sales analytics dashboard',
      'Classifieds included',
      'Customer messaging',
      'Promotions & coupon codes',
    ],
    active: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPriceCents: 24900,
    commissionBps: 600,
    maxProductListings: null,
    maxClassifiedListings: null,
    maxPropertyListings: null,
    badge: 'Best value',
    features: [
      'cardPayments',
      'analytics',
      'promotions',
      'flashSales',
      'bundles',
      'classifieds',
      'storeBanner',
      'featuredSlots',
    ],
    marketingFeatures: [
      'Unlimited product listings',
      'Unlimited classified ads',
      'Unlimited property listings',
      '6% platform commission',
      'Premium store page + SEO boost',
      'Priority placement in search',
      '3 featured listing slots/month',
      'Advanced analytics & reports',
      'Card + cash on delivery',
      'Classifieds included',
      'Promotions & coupon codes',
      'Dedicated seller support',
      'Early access to new features',
    ],
    active: true,
  },
];

export const PLAN_COMPARISON: {
  feature: string;
  starter: string | boolean;
  growth: string | boolean;
  pro: string | boolean;
}[] = [
  { feature: 'Monthly fee', starter: 'Free', growth: 'EC$99', pro: 'EC$249' },
  { feature: 'Commission per sale', starter: '10%', growth: '8%', pro: '6%' },
  { feature: 'Product listings', starter: 'Up to 10', growth: 'Up to 75', pro: 'Unlimited' },
  { feature: 'Classified ads', starter: 'Up to 10', growth: 'Up to 20', pro: 'Unlimited' },
  { feature: 'Property listings', starter: false, growth: 'Up to 25', pro: 'Unlimited' },
  { feature: 'Cash on delivery', starter: true, growth: true, pro: true },
  { feature: 'Card payments (Stripe)', starter: false, growth: true, pro: true },
  { feature: 'Store banner & bio', starter: false, growth: true, pro: true },
  { feature: 'Sales analytics', starter: false, growth: true, pro: true },
  { feature: 'Classifieds', starter: true, growth: true, pro: true },
  { feature: 'Promotions & coupons', starter: false, growth: true, pro: true },
  { feature: 'Featured listing slots', starter: false, growth: '1/month', pro: '3/month' },
  { feature: 'Priority search rank', starter: false, growth: false, pro: true },
  { feature: 'Dedicated support', starter: false, growth: false, pro: true },
];
